import { useState } from "react";
import "./dashboard.css";
import { TimeRangeControl, KpiFilterPopover } from "./dashboard-header";
import { KpiCards } from "./kpi-cards";
import { AuditPipeline } from "./audit-pipeline";
import { FindingsSeverity } from "./findings-severity";
import { FrameworkCoverage } from "./framework-coverage";
import { ExpiryWarnings } from "./expiry-warnings";
import { TeamActivity } from "./team-activity";
import { CloudUpload, Plus, FileText, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Button } from "@/components/ui/primitives";
import { useNavigate, useParams } from "@tanstack/react-router";
import type {
  TimeRange,
  KpiId,
  KpiData,
  PipelineStage,
} from "./dashboard.types";
import { DEFAULT_VISIBLE_KPIS, ALL_KPI_IDS } from "./dashboard.types";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { api } from "convex/_generated/api";
import type { AuditId, Id } from "@/packages/types";
import { AuditSelectSheet } from "@/components/audits/AuditSelectSheet";
import { NewAuditWizard } from "@/components/audits/NewAuditWizard/NewAuditWizard";
import { UploadSheet } from "@/components/documents/upload";
import { DashboardOnboarding } from "./dashboard-onboarding";

const STORAGE_KEY = "dashboard:visibleKpis";
const ORDER_KEY = "dashboard:kpiOrder";

function loadVisibleKpis(): KpiId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as KpiId[];
  } catch {
    // ignore parse errors
  }
  return DEFAULT_VISIBLE_KPIS;
}

function loadKpiOrder(): KpiId[] {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as KpiId[];
      // Validate: every known KPI must be present in the stored order
      if (ALL_KPI_IDS.every((id) => parsed.includes(id))) {
        return parsed;
      }
    }
  } catch {
    // ignore parse errors
  }
  return [...ALL_KPI_IDS];
}

const cardAnimation = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { ...springs.smooth, delay: index * 0.04 },
});

// ── KPI Response Mapping ─────────────────────────────────────────

interface KpiTrends {
  activeAudits: number;
  completedAudits: number;
  openFindings: number;
  missingEvidence: number;
  fixRate: number;
  upcomingDeadlines: number;
}

interface KpisResponse {
  activeAudits: number;
  completedAudits: number;
  openFindings: number;
  missingEvidence: number;
  fixRate: number | null;
  upcomingDeadlines: number;
  trends: KpiTrends;
}

function mapKpisToKpiData(
  raw: KpisResponse,
  translate: (key: string) => string,
): KpiData[] {
  return [
    {
      id: "activeAudits",
      value: raw.activeAudits,
    },
    {
      id: "completedAudits",
      value: raw.completedAudits,
      trend: raw.trends.completedAudits,
    },
    {
      id: "openFindings",
      value: raw.openFindings,
      invertTrend: true,
      trend: raw.trends.openFindings,
    },
    {
      id: "missingEvidence",
      value: raw.missingEvidence,
      invertTrend: true,
      trend: raw.trends.missingEvidence,
    },
    {
      id: "fixRate",
      value: raw.fixRate ?? 0,
      unit: translate("dashboard:fixRateUnit"),
      trend: raw.trends.fixRate,
    },
    { id: "upcomingDeadlines", value: raw.upcomingDeadlines, trend: 0 },
  ];
}

// ── Pipeline Response Mapping ────────────────────────────────────

import { getPaletteColor } from "@/lib/color-palettes";

const STAGE_SLOT: Record<string, number> = {
  pending: 1,
  in_progress: 2,
  submitted: 3,
  approved: 4,
  rejected: 5,
};

function getStageColor(stage: string): string {
  return getPaletteColor("audit-status", STAGE_SLOT[stage] ?? 1);
}

interface PipelineBackendStage {
  stage: string;
  count: number;
  audits: {
    id?: Id<"audits">;
    name: string;
    deadline: number;
    templateName: string;
    trigger: string;
  }[];
}

function formatDate(timestamp: number): string {
  const d = new Date(timestamp);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function mapPipelineData(
  raw: PipelineBackendStage[],
  translate: (key: string) => string,
): PipelineStage[] {
  return raw.map((stage) => ({
    status: translate(`dashboard:pipeline.status.${stage.stage}`),
    count: stage.count,
    color: getStageColor(stage.stage),
    isCompleted: stage.stage === "approved",
    audits: stage.audits.map((a) => ({
      id: a.id,
      name: a.name,
      client: a.trigger,
      due: formatDate(a.deadline),
      fw: a.templateName,
    })),
  }));
}

// ── DashboardPage ────────────────────────────────────────────────

export function DashboardPage() {
  const { t } = useTranslation(["dashboard"]);
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const [visibleKpis, setVisibleKpis] = useState<KpiId[]>(loadVisibleKpis);
  const [kpiOrder, setKpiOrder] = useState<KpiId[]>(loadKpiOrder);
  const [showFindingSelect, setShowFindingSelect] = useState(false);
  const [showReportSelect, setShowReportSelect] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const onboardingQuery = useWorkspaceQuery(
    api.dashboard.queries.onboarding.get,
    {},
  );

  // Wrap t to avoid TS compiler crash on overloaded signature resolution
  // biome-ignore lint/suspicious/noExplicitAny: workaround for TS compiler crash on overloaded i18n signature
  const simpleT = (key: string) => t(key as any);

  const kpisQuery = useWorkspaceQuery(api.dashboard.queries.kpis.get, {
    timeRange,
  });
  const pipelineQuery = useWorkspaceQuery(
    api.dashboard.queries.pipeline.get,
    {},
  );

  const kpiData = kpisQuery.data
    ? mapKpisToKpiData(kpisQuery.data, simpleT)
    : null;
  const pipelineData = pipelineQuery.data
    ? mapPipelineData(pipelineQuery.data, simpleT)
    : null;

  const handleVisibleKpisChange = (newVisible: KpiId[]) => {
    const added = newVisible.filter((id) => !visibleKpis.includes(id));

    let nextOrder = kpiOrder;
    if (added.length > 0) {
      // Newly enabled cards go to end of order
      nextOrder = [...kpiOrder.filter((id) => !added.includes(id)), ...added];
      setKpiOrder(nextOrder);
      localStorage.setItem(ORDER_KEY, JSON.stringify(nextOrder));
    }

    setVisibleKpis(newVisible);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newVisible));
  };

  const handleResetOrder = () => {
    const defaultOrder = [...ALL_KPI_IDS];
    setKpiOrder(defaultOrder);
    localStorage.setItem(ORDER_KEY, JSON.stringify(defaultOrder));
  };

  const handleKpiOrderChange = (newOrder: KpiId[]) => {
    setKpiOrder(newOrder);
    localStorage.setItem(ORDER_KEY, JSON.stringify(newOrder));
  };

  const handlePipelineAuditClick = (auditId: Id<"audits">) => {
    navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: {
        workspaceSlug: workspaceSlug ?? "",
        auditId,
      },
    });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="p-4 lg:p-6 w-full flex flex-col flex-1 min-h-0">
        <motion.div
          className="flex items-center justify-between mb-3"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springs.smooth}
        >
          <h1 className="text-[24px] font-bold text-primary shrink-0">
            {t("dashboard:title")}
          </h1>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="secondary"
              icon={CloudUpload}
              onClick={() => setShowUpload(true)}
            >
              {t("dashboard:actions.uploadEvidence")}
            </Button>
            <Button
              variant="secondary"
              icon={AlertTriangle}
              onClick={() => setShowFindingSelect(true)}
            >
              {t("dashboard:actions.reportFinding")}
            </Button>
            <Button
              variant="secondary"
              icon={FileText}
              onClick={() => setShowReportSelect(true)}
            >
              {t("dashboard:actions.createReport")}
            </Button>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setIsWizardOpen(true)}
            >
              {t("dashboard:actions.newAudit")}
            </Button>
          </div>
        </motion.div>
        {onboardingQuery.data &&
        !(
          onboardingQuery.data.hasAudits &&
          onboardingQuery.data.hasDocuments &&
          onboardingQuery.data.hasChecklistActivity
        ) ? (
          <DashboardOnboarding
            onboarding={onboardingQuery.data}
            onOpenWizard={() => setIsWizardOpen(true)}
            onOpenUpload={() => setShowUpload(true)}
          />
        ) : (
          <>
            <div className="rounded-xl border border-border bg-card p-4 shrink-0">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-primary">
                  {t("dashboard:overview.title")}
                </h2>
                <div className="flex items-center gap-2">
                  <TimeRangeControl
                    timeRange={timeRange}
                    onTimeRangeChange={setTimeRange}
                  />
                  <KpiFilterPopover
                    visibleKpis={visibleKpis}
                    onVisibleKpisChange={handleVisibleKpisChange}
                    onResetOrder={handleResetOrder}
                  />
                </div>
              </div>
              <KpiCards
                data={kpiData}
                isLoading={kpisQuery.isLoading}
                visibleKpis={visibleKpis}
                kpiOrder={kpiOrder}
                onKpiOrderChange={handleKpiOrderChange}
                timeRange={timeRange}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 flex-1 min-h-0 lg:grid-rows-[minmax(320px,1fr)_minmax(320px,1fr)]">
              <motion.div
                {...cardAnimation(0)}
                className="h-full min-h-[320px] max-md:min-h-[480px]"
              >
                <AuditPipeline
                  data={pipelineData}
                  isLoading={pipelineQuery.isLoading}
                  onAuditClick={handlePipelineAuditClick}
                />
              </motion.div>
              <motion.div
                {...cardAnimation(1)}
                className="h-full min-h-[320px]"
              >
                <FrameworkCoverage />
              </motion.div>
              <motion.div
                {...cardAnimation(2)}
                className="h-full min-h-[320px]"
              >
                <ExpiryWarnings />
              </motion.div>
              <motion.div
                {...cardAnimation(3)}
                className="h-full min-h-[320px]"
              >
                <TeamActivity />
              </motion.div>
              <motion.div
                {...cardAnimation(4)}
                className="md:col-span-2 h-full min-h-[320px] max-md:min-h-[480px]"
              >
                <FindingsSeverity />
              </motion.div>
            </div>
          </>
        )}
      </div>
      <AuditSelectSheet
        open={showFindingSelect}
        onClose={() => setShowFindingSelect(false)}
        title={t("dashboard:auditSelect.finding.title")}
        description={t("dashboard:auditSelect.finding.description")}
        onSelect={(auditId: AuditId) => {
          navigate({
            to: "/$workspaceSlug/audits/$auditId",
            params: { workspaceSlug: workspaceSlug ?? "", auditId },
            search: { tab: "findings" },
          });
        }}
      />
      <AuditSelectSheet
        open={showReportSelect}
        onClose={() => setShowReportSelect(false)}
        title={t("dashboard:auditSelect.report.title")}
        description={t("dashboard:auditSelect.report.description")}
        onSelect={(auditId: AuditId) => {
          navigate({
            to: "/$workspaceSlug/audits/$auditId",
            params: { workspaceSlug: workspaceSlug ?? "", auditId },
            search: { tab: "overview", openReport: true },
          });
        }}
      />
      <NewAuditWizard
        open={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
      />
      <UploadSheet open={showUpload} onClose={() => setShowUpload(false)} />
    </div>
  );
}
